// src/components/user/controltower/RegularDeliveryCard.jsx
import React from 'react';
import { AlertTriangle, Clock, MapPin, Phone } from 'lucide-react';
import { getAlertLabel } from '../../delivery-alerts/alertTypes';

const STATUS_STYLES = {
  pending: { bar: 'bg-orange-400', badge: 'bg-orange-100 text-orange-700', label: 'Pendiente' },
  encamino: { bar: 'bg-blue-400', badge: 'bg-blue-100 text-blue-700', label: 'En Camino' },
  entregado: { bar: 'bg-green-400', badge: 'bg-green-100 text-green-700', label: 'Entregado' },
  cancelado: { bar: 'bg-red-400', badge: 'bg-red-100 text-red-700', label: 'Cancelado' },
  rechazado: { bar: 'bg-red-400', badge: 'bg-red-100 text-red-700', label: 'Rechazado' },
};

function highestSeverity(alerts = []) {
  if (alerts.some(alert => alert.severity === 'high')) return 'high';
  if (alerts.some(alert => alert.severity === 'medium')) return 'medium';
  if (alerts.some(alert => alert.severity === 'low')) return 'low';
  return null;
}

function alertClasses(severity) {
  if (severity === 'high') {
    return 'bg-red-50 text-red-700 border-red-200';
  }
  if (severity === 'low') {
    return 'bg-slate-100 text-slate-700 border-slate-200';
  }
  return 'bg-amber-50 text-amber-700 border-amber-200';
}

export function RegularDeliveryCard({ delivery, onClick, isSelected, alerts = [] }) {
  const s = STATUS_STYLES[delivery.status] || {
    bar: 'bg-slate-300',
    badge: 'bg-slate-100 text-slate-600',
    label: delivery.status,
  };
  const severity = highestSeverity(alerts);
  const primaryAlert = alerts[0];

  return (
    <button
      onClick={onClick}
      className={`w-full text-left flex items-stretch transition-all ${
        isSelected
          ? 'bg-brand-primary/5 shadow-[inset_0_0_0_1px_rgba(177,51,110,0.16)]'
          : alerts.length > 0
            ? 'bg-amber-50/30 hover:bg-amber-50/60'
            : 'hover:bg-slate-50'
      }`}
    >
      <div className={`w-1 flex-shrink-0 ${severity === 'high' ? 'bg-red-500' : severity ? 'bg-amber-400' : s.bar}`} />

      <div className="flex-1 px-3 py-3 space-y-2 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-xs font-bold text-slate-400 font-montserrat shrink-0">#{delivery.id}</span>
            <p className="text-sm font-bold text-slate-900 font-montserrat truncate">{delivery.recipient_name}</p>
          </div>
          <span className={`text-[11px] sm:text-xs font-semibold px-2 py-0.5 rounded-full font-montserrat shrink-0 ${s.badge}`}>
            {s.label}
          </span>
        </div>

        {alerts.length > 0 && (
          <div className={`flex items-center gap-1.5 rounded-xl border px-2 py-1 ${alertClasses(severity)}`}>
            <AlertTriangle className={`w-3.5 h-3.5 shrink-0 ${severity === 'high' ? 'animate-pulse' : ''}`} />
            <span className="text-[11px] font-extrabold font-montserrat shrink-0">
              {alerts.length} alerta{alerts.length !== 1 ? 's' : ''}
            </span>
            <span className="text-[11px] font-semibold font-montserrat truncate">
              {getAlertLabel(primaryAlert?.alert_type)}
            </span>
          </div>
        )}

        <div className="flex items-start gap-1.5">
          <MapPin className="w-3 h-3 text-slate-400 mt-0.5 shrink-0" />
          <p className="text-xs text-slate-600 font-montserrat line-clamp-1">
            {delivery.address}, {delivery.district}
          </p>
        </div>

        <div className="flex items-center gap-3 min-w-0">
          {delivery.recipient_phone && (
            <div className="flex items-center gap-1 min-w-0">
              <Phone className="w-3 h-3 text-slate-400 shrink-0" />
              <span className="text-xs text-slate-500 font-montserrat truncate">{delivery.recipient_phone}</span>
            </div>
          )}
          {delivery.arrival_time && (
            <div className="flex items-center gap-1 ml-auto shrink-0">
              <Clock className="w-3 h-3 text-slate-400" />
              <span className="text-xs text-slate-500 font-montserrat">
                {new Date(delivery.arrival_time).toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          )}
        </div>
      </div>
    </button>
  );
}
